import { buildMenuTooltip } from '../../../helpers/menuHelpers'
import { taskActions } from '../helpers'

const shouldDisableDelete = (phase) => phase.isLocked || phase.itemCount <= 1

function defaultMenu(task, phase) {
  const deleteText = `Delete ${task.isComplete ? 'completed ' : ''}task`
  let deleteTaskTooltip = false
  if (phase.isLocked) {
    const tooltipText =
      'This task cannot be deleted because it is in a locked phase.'
    deleteTaskTooltip = buildMenuTooltip(tooltipText)
  }

  return [
    {
      title: deleteText,
      action: taskActions.Delete,
      disabled: shouldDisableDelete(phase),
      tooltip: deleteTaskTooltip,
    },
  ]
}

function buildIntegrationOption(
  task,
  phase,
  providerName,
  workspaceIntegration,
  project,
  integrationTask = null
) {
  let option = []

  // This task is already complete without going to WF, no need to display integration options
  if (integrationTask === null && task.isComplete) return []

  const isEnabled =
    workspaceIntegration.enabled && !phase.isComplete && phase.isCurrent

  const providerNameCapitalized =
    providerName[0].toUpperCase() + providerName.slice(1)

  const state = {
    error: null,
    initial: {
      action: taskActions.CreateIntegrationTask,
      disabled: false,
      title: `Send Task to ${project.provider_side_name}`,
    },
    sending: {
      action: null,
      disabled: true,
      title: `Sending to ${providerNameCapitalized}...`,
    },
    sent: {
      action: taskActions.ViewInProvider,
      disabled: false,
      title: `View in ${providerNameCapitalized}`,
    },
  }

  if (isEnabled) {
    let currentState = state.initial

    if (integrationTask) {
      if (integrationTask.provider_error) {
        // Error handling will be displayed in the avatar popover.
        currentState = state.error
      } else if (integrationTask.provider_url) {
        // IT has been sent to WF, but not completed
        currentState = state.sent
      } else {
        // IT is in Opal, and should be on its way to WF
        currentState = state.sending
      }
    }

    if (currentState !== state.error) {
      option = [
        {
          action: currentState.action,
          disabled: currentState.disabled,
          displayIconRight: true,
          icon: 'url',
          title: currentState.title,
          workspaceIntegrationUuid:
            workspaceIntegration.workspaceIntegrationUuid,
          projectUuid: project.uuid,
        },
      ]
    }
  }

  return option
}

function buildIntegrationOptions(
  task,
  phase,
  workspaceIntegrations = {},
  integrationTask = null
) {
  const options = []
  const projectManagementProviders = ['jira', 'workfront', 'wrike']

  projectManagementProviders.forEach((provider) => {
    if (workspaceIntegrations[provider]) {
      workspaceIntegrations[provider].projects.forEach((project) => {
        // Build a menu to deliver tasks if the integrationTask has not attempted delivery
        // If it has delivery has been attempted (successfully or not) only build the menu option for the
        // integration and project we attempted delivery for
        if (
          integrationTask === null ||
          (integrationTask.workspace_integration_uuid ===
            workspaceIntegrations[provider].workspaceIntegrationUuid &&
            integrationTask.project_uuid === project.uuid)
        ) {
          options.push(
            ...buildIntegrationOption(
              task,
              phase,
              provider,
              workspaceIntegrations[provider],
              project,
              integrationTask
            )
          )
        }
      })
    }
  })

  return options
}

export const buildMenu = (
  task,
  phase,
  enabledIntegrations,
  integrationTask
) => [
  ...buildIntegrationOptions(task, phase, enabledIntegrations, integrationTask),
  ...defaultMenu(task, phase),
]

let previousPhaseState
let previousTaskState
let previousIntegrationTaskState
/**
 * Compares the states of the Task and Phase to see if any relevent changes have occurred
 * for which we need to force a re-render of the Menu list.
 */
export function phaseHasUpdates(phaseState, taskState, integrationTaskState) {
  if (!previousPhaseState || !previousTaskState) {
    previousPhaseState = phaseState
    previousTaskState = taskState
    previousIntegrationTaskState = integrationTaskState
    return true
  }

  const phaseHasUpdated = previousPhaseState.itemCount !== phaseState.itemCount
  const taskHasUpdated = previousTaskState.isComplete !== taskState.isComplete
  const integrationTaskHasUpdated =
    previousIntegrationTaskState !== integrationTaskState

  previousPhaseState = phaseState
  previousTaskState = taskState
  previousIntegrationTaskState = integrationTaskState

  return phaseHasUpdated || taskHasUpdated || integrationTaskHasUpdated
}

export default {
  phaseHasUpdates,
}
